import React, { memo, PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { useAppContext } from "../../../hooks/useAppContext";
import { useErrorHandling } from "../../../hooks/useErrorHandling";
import { useGoogleAnalytics } from "../../../hooks/useGoogleAnalytics";
import { useIsDarkForced } from "../../../hooks/useIsDarkForced";
import { useIsMobileDevice } from "../../../hooks/useIsMobileDevice";
import { useLabels } from "../../../hooks/useLabels";
import { useNavigateToUrl } from "../../../hooks/useNavigate";
import { labels } from "./labels";
import { UnsuportedBrowserDialog } from "./unsupported-browser-dialog";

export const UnsuportedBrowserDialogContainer = memo(function UnsuportedBrowserDialogContainer({
	children,
}: PropsWithChildren) {
	const { platform, client } = useAppContext();
	const { queryString, cookies } = platform;
	const { createErrorHandler } = useErrorHandling();
	const [showOtherEditorButton, setShowOtherEditorButton] = useState(true);
	const isMobileDevice = useIsMobileDevice();

	const googleAnalytics = useGoogleAnalytics();
	const { data: isDarkForced } = useIsDarkForced();

	useEffect(() => {
		setShowOtherEditorButton(!isDarkForced);
		if (!isDarkForced) return;
		// Log GA event when dark mode is detected
		googleAnalytics.trackEventToGa({
			trackingAction: "ForcedDarkMode",
		});
	}, [googleAnalytics, isDarkForced]);

	const logErrorMessage = useCallback(
		(errorMessage: string, errorType: string) => {
			client
				.logErrorMessageAsync(
					errorMessage,
					"",
					"",
					errorType,
					eXpress.core.LogProcess.MobileCreator,
				)
				.catch(createErrorHandler());
		},
		[client, createErrorHandler],
	);

	// Check once
	const doesSupportBrowserFeatures = useMemo(() => {
		// code from https://mathiasbynens.be/notes/localstorage-pattern
		const supportLocalStorage = (function x() {
			const uid = new Date().toString();
			let storage;
			let result;
			try {
				(storage = window.localStorage).setItem(uid, uid);
				result = storage.getItem(uid) === uid;
				storage.removeItem(uid);
				return result && storage;
			} catch (_exception) {
				return false;
			}
		})();

		if (!window.requestAnimationFrame) {
			logErrorMessage("RequestAnimationFrame is not supported.", "MissingBrowserFeature");
			return false;
		}

		if (!supportLocalStorage) {
			logErrorMessage("LocalStorage is not supported.", "MissingBrowserFeature");
			return false;
		}

		if (!Promise.prototype.finally) {
			logErrorMessage("Promise.prototype.finally is not supported.", "MissingBrowserFeature");
			return false;
		}

		return true;
	}, [logErrorMessage]);

	// This check also happens on the server (EditorDispatcher). Check on the client for special cases (bookmarks)
	// regex codes based on https://gist.github.com/lyquix-owner/34a3ed98b990c3c61151d30de2f42702
	// default value is true. Only return false if we know the browser version is not ok for using the MC
	const isSupportedUserAgent = useMemo(() => {
		function getVersion(text: string, regex: RegExp) {
			const match = text.match(regex);
			const version = (match && match.length > 1 && match[1]) || "";
			return (version && parseInt(version, 10)) || undefined;
		}

		const ua = window.navigator.userAgent;
		const userAgent = ua ? ua.toLowerCase() : "";

		if (/msie|trident/i.test(userAgent)) {
			// IE
			logErrorMessage("IE browser is not supported in MC.", "BrowserNotSupported");
			return false;
		}

		if (/chrome|crios|crmo/i.test(userAgent)) {
			// Chrome
			// At least version 80
			const version = getVersion(userAgent, new RegExp(/(?:chrome|crios|crmo)\/(\d+)/i));
			const minVersion = 80;
			if (version) {
				if (version < minVersion) {
					// Dont log for google crawler
					const isGoogleCrawler = userAgent.includes("google-read-aloud;");
					if (!isGoogleCrawler) {
						logErrorMessage(
							`Chrome ${version} browser (min supported version is ${minVersion}) is not supported in MC.`,
							"BrowserNotSupported",
						);
					}
					return false;
				}

				return true;
			}
		}
		if (/firefox/i.test(userAgent)) {
			// Firefox
			// At least version 80
			const version = getVersion(userAgent, new RegExp(/(?:firefox)\/(\d+)/i));
			const minVersion = 80;
			if (version) {
				if (version < minVersion) {
					logErrorMessage(
						`Firefox ${version} browser (min supported version is ${minVersion}) is not supported in MC.`,
						"BrowserNotSupported",
					);
					return false;
				}

				return true;
			}
		}
		if (/fxios/i.test(userAgent)) {
			// Firefox for ios
			// At least version 20
			const version = getVersion(userAgent, new RegExp(/(?:fxios)\/(\d+)/i));
			const minVersion = 20;
			if (version) {
				if (version < minVersion) {
					logErrorMessage(
						`Firefox ios ${version} browser (min supported version is ${minVersion}) is not supported in MC.`,
						"BrowserNotSupported",
					);
					return false;
				}

				return true;
			}
		}
		if (/safari/i.test(userAgent)) {
			// Safari
			// At least version 14
			const version = getVersion(userAgent, new RegExp(/version\/(\d+)/i));
			const minVersion = 14;
			if (version) {
				if (version < minVersion) {
					logErrorMessage(
						`Safari ${version} browser (min supported version is ${minVersion}) is not supported in MC.`,
						"BrowserNotSupported",
					);
					return false;
				}

				return true;
			}
		}

		// If the device model has 'edge' in the name it will be picked up as an edge browser. Check other browsers first to so we can early exit
		// if we come across a supported browser
		if (/edge/i.test(userAgent)) {
			// The Edge browser NOT based on webkit
			logErrorMessage("Edge browser is not supported in MC.", "BrowserNotSupported");
			return false;
		}
		if (/edg/i.test(userAgent)) {
			// The Edge webkit browser
			logErrorMessage("Edge browser is not supported in MC.", "BrowserNotSupported");
			return false;
		}

		return true;
	}, [logErrorMessage]);

	// Get labels
	const [[title = "", message = "", goToOtherEditor = "", goBack = ""], isLoadingLabels] =
		useLabels([
			labels.title,
			isDarkForced ? labels.darkModeMessage : labels.message,
			labels.goToOtherEditor,
			labels.goBack,
		]);

	const navigateToUrl = useNavigateToUrl();

	// browser features not supported => set cookie "UnsupportedEditors" with expire date of 7 days (10080 min)
	// UnsupportedEditors cookie can have values creator and mobilecreator
	let unsupportedEditors = (cookies.getCookie("UnsupportedEditors") || "")
		.split(",")
		.map((editorName) => editorName.trim())
		.filter((editorName) => !!editorName); // spliting an empty string gives [""]
	const hasMobileCreator = unsupportedEditors.includes("mobilecreator");

	if (!doesSupportBrowserFeatures) {
		if (!hasMobileCreator) {
			unsupportedEditors.push("mobilecreator");
			cookies.setCookie("UnsupportedEditors", unsupportedEditors.join(","), {
				minutesBeforeExpire: 10080, // 1 week
				path: "/",
			});
		}
	} else if (hasMobileCreator) {
		unsupportedEditors = unsupportedEditors.filter(
			(editorName) => editorName !== "mobilecreator",
		);
		cookies.setCookie("UnsupportedEditors", unsupportedEditors.join(","), {
			minutesBeforeExpire: 10080, // 1 week
			path: "/",
		});
	}

	// set "IsMobileDevice" cookie
	if (isMobileDevice) {
		if (cookies.getCookie("IsMobileDevice") !== "true") {
			cookies.setCookie("IsMobileDevice", "true", {
				minutesBeforeExpire: 365 * 24 * 60,
				path: "/",
			});
		}
	} else if (cookies.getCookie("IsMobileDevice") !== "false") {
		cookies.setCookie("IsMobileDevice", "false", {
			minutesBeforeExpire: 365 * 24 * 60,
			path: "/",
		});
	}

	if (doesSupportBrowserFeatures && isSupportedUserAgent && !isDarkForced) return <>{children}</>; // No problem, continue
	if (isLoadingLabels) return null; // Not supported and labels are still loading

	return (
		<UnsuportedBrowserDialog
			title={title}
			message={message}
			goToOtherEditor={goToOtherEditor}
			goBack={goBack}
			onGoToOtherEditor={
				showOtherEditorButton
					? () => {
							let url = window.location.href;

							// Remove fragemnt
							const fragmentIndex = url.lastIndexOf("#");
							if (fragmentIndex >= 0) url = url.substring(0, fragmentIndex);

							// add editor
							url = queryString.addOrReplaceQueryStringParameterInUrl(
								url.replace("mobilecreator", "creator"),
								"editor",
								"creator",
							);

							navigateToUrl(url);
					  }
					: undefined
			}
			onGoBack={() => window.history.back()}
		/>
	);
});
